import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";

import { type FandomCmp, LoadTimeEvent } from "common-types";
import { loadScript, pushTrack } from "utils";
import type {
  AssertiveYieldPluginConfig,
  AssertiveYieldWindowObject,
} from "./types";
import { isConsentGranted } from "../../helpers/isConsentGranted";
import { isBidderAdUnit } from "../../config/platforms/types";

const ENTITY_ID = "grbqPYtkDC5HPMrgd";
const SCRIPT_URL = `//${ENTITY_ID}.ay.delivery/client-v2.js`;

/**
 * AssertiveYield plugin for AdEngine.
 *
 * - Requires consent
 * - It's installed manually (separately).
 * - There is also a Prebid module for setting Price Floors, work coming soon
 * - AssertiveYield is used to provide Prebid price floors.
 *
 * @see https://docs.prebid.org/dev-docs/modules/floors.html
 * @see https://suite.assertiveyield.com/docs/dynamic-flooring/dynamic-flooring/#dynamic-flooring
 *
 * For dynamic debug:
 * @see https://suite.assertiveyield.com/docs/yield-intelligence/tracking-client-documentation#debug-mode
 */
export class AssertiveYieldPlugin extends AdEnginePlugin {
  private readonly floor?: AssertiveYieldPluginConfig["icAssertiveYieldFloors"];
  // Tracks whether the script was loaded into the page. This prevents multiple loads whenever consents change.
  private isLoaded = false;

  constructor(private readonly config: AssertiveYieldPluginConfig) {
    super();

    if (config.icAssertiveYieldFloors?.enabled) {
      this.floor = config.icAssertiveYieldFloors;
    }
  }

  static isEnabled(config: AssertiveYieldPluginConfig): boolean {
    return config?.icAssertiveYield;
  }

  /**
   * Creates the AssertiveYield object
   */
  private createAssertiveYieldWindowObject() {
    window.assertive ??= {
      debug: false,
      entityId: ENTITY_ID,
      analytics: {
        integrations: {
          webAnalytics: true,
        },
        logUnfilled: true,
        custom: {
          layout: null,
          userState: null,
          custom_1: null,
          custom_2: null,
          custom_3: null,
          custom_4: null,
        },
      },
      hash: {
        generator: "server",
        values: 1,
        key: "ay",
      },
    } as AssertiveYieldWindowObject;

    if (this.floor) {
      window.assertive.floor = this.floor;
    }
  }

  /**
   * Creates and inserts the AssertiveYield script into the document.
   * This method ensures the script is loaded only once and handles any errors during the script loading process.
   *
   * @returns {Promise<void>} A promise that resolves when the script is successfully loaded.
   */
  private async createAndInsertScript() {
    try {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "assertive-yield_started",
        time: performance.now(),
      });
      await loadScript(SCRIPT_URL);
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "assertive-yield_loaded",
        time: performance.now(),
      });
      this.isLoaded = true;
    } catch (e) {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "assertive-yield_error_loading",
        time: performance.now(),
      });
      console.error("Failed to load AssertiveYield script", e);
    }
  }

  /**
   * Initializes the AssertiveYield plugin by setting up the window object and loading the script.
   * This won't run if the ICBM var icAssertiveYield is not enabled.
   *
   * The isLoaded member variable is used to prevent multiple loads of the script, especially when consent is updated.
   */
  private init() {
    if (this.isLoaded) {
      return;
    }

    this.createAssertiveYieldWindowObject();
    this.createAndInsertScript();
  }

  hooks: Hooks = {
    /**
     * Hook that runs when consent is updated.
     * @param {FandomCmp} fandomCmp - The FandomCmp instance containing consent information.
     */
    onConsentUpdated: (fandomCmp: FandomCmp) => {
      if (!isConsentGranted(fandomCmp)) {
        return;
      }

      this.init();
    },

    /**
     * Hook that runs when consent is updated.
     * @param {BidderSource} bidderSource - The FandomCmp instance containing consent information.
     * @param {AuctionData} auctionData - The FandomCmp instance containing consent information.
     * @param {BidderAdUnitConfigMap[S extends BidderSource]} adUnitConfig - The FandomCmp instance containing consent information.
     */
    onCreateBidderAdUnit: (bidderSource, auctionData, adUnitConfig) => {
      if (isBidderAdUnit("a9", bidderSource, adUnitConfig)) {
        if (
          window.assertive &&
          window.assertive.addAmazonFloors &&
          window.assertive.floor?.enabled
        ) {
          window.assertive.addAmazonFloors(adUnitConfig.slots);
        }
      }
    },
  };
}
